import React, { useState } from 'react';
import { useSubscription, SUBSCRIPTION_TIERS, TIER_CONFIG } from '../../context/SubscriptionContext';
import MockCheckout from './MockCheckout';
import { Check, X, Crown, Star, Zap, ArrowLeft } from 'lucide-react';
import './Subscription.css';

const UpgradePage = ({ onBack }) => {
  const { currentTier } = useSubscription();
  const [selectedTier, setSelectedTier] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  const tierOrder = [
    SUBSCRIPTION_TIERS.FREE,
    SUBSCRIPTION_TIERS.BASIC,
    SUBSCRIPTION_TIERS.PRO,
    SUBSCRIPTION_TIERS.ADVANCED,
    SUBSCRIPTION_TIERS.SUPER
  ];

  const featureLabels = {
    wizardGF: 'Wizard A: Research Gap Finder',
    wizardA: 'Wizard B: Research Methodology',
    wizardB: 'Wizard C: Data Analysis Planning',
    learnBasics: 'Framework - Learn the Basics',
    wizardC: 'Wizard D: Framework Builder',
    wizardD: 'Wizard E: Data Collection Process',
    wizardE: 'Wizard F: Ethics Approval',
    reportA: 'Research Gap Finder Report',
    reportB: 'Methodology Report',
    reportC: 'Data Analysis Report',
    reportD: 'Framework Report',
    reportE: 'Ethics Approval Report',
    reportCombined: 'Combined Research Report',
    compareMethodology: 'Create and Compare Different Methodologies'
  };

  const limitedLabels = {
    wizardGF: 'Steps 1–3 only',
    wizardA: 'Steps 1–4 only',
    wizardB: 'Steps 1–4 only',
    wizardC: 'Steps 1–4 only',
  };

  const getFeatureStatus = (tier, feature) => {
    const config = TIER_CONFIG[tier];
    const value = config.features[feature];
    
    if (value === true) return 'full';
    if (value === false) return 'none';
    if (typeof value === 'object' && value.maxStep !== undefined) {
      return value.maxStep >= 999 ? 'full' : 'limited';
    }
    return 'none';
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case SUBSCRIPTION_TIERS.SUPER:
        return <Crown size={24} />;
      case SUBSCRIPTION_TIERS.ADVANCED:
        return <Star size={24} />;
      case SUBSCRIPTION_TIERS.PRO:
        return <Zap size={24} />;
      default:
        return null;
    }
  };

  const handleSelectTier = (tier) => {
    if (tier === SUBSCRIPTION_TIERS.FREE || tier === currentTier) return;
    setSelectedTier(tier);
    setShowCheckout(true);
  };

  const handleCheckoutComplete = () => {
    setShowCheckout(false);
    setSelectedTier(null);
  };

  if (showCheckout && selectedTier) {
    return (
      <MockCheckout 
        tier={selectedTier} 
        onComplete={handleCheckoutComplete}
        onCancel={() => setShowCheckout(false)}
      />
    );
  }

  return (
    <div className="upgrade-page">
      <div className="upgrade-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={20} />
          Back to Wizard
        </button>
        <h1>Choose Your Plan</h1>
        <p>Select the plan that best fits your research needs</p>
      </div>

      <div className="tier-cards-container">
        {tierOrder.map((tier) => {
          const config = TIER_CONFIG[tier];
          const isCurrentTier = tier === currentTier;
          const isPopular = config.badge === 'Popular Choice';
          const isValue = config.badge === 'Excellent Value';

          return (
            <div 
              key={tier}
              className={`tier-card ${isCurrentTier ? 'current' : ''} ${isPopular ? 'popular' : ''} ${isValue ? 'value' : ''}`}
            >
              {config.badge && (
                <div className={`tier-badge ${isPopular ? 'popular' : 'value'}`}>
                  {config.badge}
                </div>
              )}
              
              <div className="tier-header">
                <div className="tier-icon">{getTierIcon(tier)}</div>
                <h2>{config.name}</h2>
                <div className="tier-price">
                  {config.discount && (
                    <span className="original-price">{config.originalPrice}</span>
                  )}
                  <span className="current-price">{config.displayPrice}</span>
                  {config.discount && (
                    <span className="discount-badge">{config.discount}</span>
                  )}
                  {tier === SUBSCRIPTION_TIERS.SUPER && (
                    <span className="per-month-savings">Only $5/month vs $6/month</span>
                  )}
                </div>
                <p className="tier-description">{config.description}</p>
              </div>

              <div className="tier-features">
                {Object.entries(featureLabels).map(([feature, label]) => {
                  const status = getFeatureStatus(tier, feature);
                  return (
                    <div key={feature} className={`feature-row ${status}`}>
                      {status === 'full' && <Check size={16} className="feature-icon check" />}
                      {status === 'limited' && <span className="feature-icon limited">{limitedLabels[feature] || 'Limited'}</span>}
                      {status === 'none' && <X size={16} className="feature-icon x" />}
                      <span className="feature-label">{label}</span>
                    </div>
                  );
                })}
              </div>

              <button 
                className={`tier-select-btn ${isCurrentTier ? 'current' : ''}`}
                onClick={() => handleSelectTier(tier)}
                disabled={isCurrentTier || tier === SUBSCRIPTION_TIERS.FREE}
              >
                {isCurrentTier ? 'Current Plan' : tier === SUBSCRIPTION_TIERS.FREE ? 'Free Plan' : 'Select Plan'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="upgrade-footer">
        <p>All plans include automatic saving and progress tracking.</p>
        <p className="demo-notice">This is a demo payment system for testing purposes.</p>
      </div>
    </div>
  );
};

export default UpgradePage;
